import React, {
  FC,
  useState,
  useContext,
  useEffect,
  useMemo,
  FormEvent,
} from "react";
import firebase from "firebase/app";
import * as Sentry from "@sentry/react";
import { PencilIcon } from "@heroicons/react/solid";
import { CheckIcon } from "@heroicons/react/outline";
import { StyledProfile, Password } from "..";
import { Controller, InputDialog, LabelWrapper, Spinner } from "../../System";
import { userStore } from "../../utils/UserContext";
import { useNotifications } from "../../Notifications";
import { useForm } from "../../utils";
import { profileValidation } from "./profileValidation";

const Profile: FC = () => {
  const form = useForm({ shouldUnregister: false });
  const validation = useMemo(() => profileValidation, []);

  const { user } = useContext(userStore);
  const { addNotification } = useNotifications();

  const [isEditable, setIsEditable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(
    false
  );

  const updateLoggedInUser = firebase
    .functions()
    .httpsCallable("updateLoggedInUser");

  const isDisabled = () => {
    if (!user) {
      return true;
    }

    if (
      form.values.email === user.email &&
      form.values.displayName === user.displayName
    ) {
      return true;
    }

    return false;
  };

  const cancelEdit = () => {
    form.setValue("displayName", user?.displayName || "");
    form.setValue("email", user?.email || "");
    setIsEditable(false);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setIsEditable(false);

      const isValid = await form.trigger();

      if (!isValid) {
        setIsEditable(true);
        return;
      }

      const { email, displayName } = form.values;

      const profile = firebase.auth().currentUser;
      const nameChanged = user?.displayName !== displayName;
      const emailChanged = user?.email !== email;

      // Require password confirmation for email change
      if (emailChanged && !password.trim()) {
        return setShowPasswordConfirmation(true);
      }

      if (nameChanged) {
        await profile?.updateProfile({ displayName: displayName.trim() });
      }

      if (emailChanged) {
        const credential = firebase.auth.EmailAuthProvider.credential(
          profile?.email as string,
          password
        );
        await profile?.reauthenticateWithCredential(credential);
        await profile?.updateEmail(email);
      }

      await updateLoggedInUser({
        email,
        displayName,
      });

      window.Intercom("update", {
        name: displayName,
        email,
      });

      addNotification({
        message: "Profile Updated!",
        type: "success",
        interval: 5000,
      });
    } catch (err) {
      setIsEditable(true);
      addNotification({
        message:
          (err as any)?.message || "There was a problem updating your profile",
        type: "error",
      });
      Sentry.captureException(err);
    } finally {
      setPassword("");
      setLoading(false);
    }
  };

  useEffect(() => {
    form.setValue("displayName", user?.displayName || "");
    form.setValue("email", user?.email || "");
  }, [user]);

  return (
    <>
      <StyledProfile>
        <div className="account-settings-group-header">
          <h3>User Information</h3>
          {!isEditable && (
            <button
              type="button"
              disabled={loading}
              onClick={() => setIsEditable(true)}
            >
              {loading ? (
                <Spinner size="small" />
              ) : (
                <>
                  <PencilIcon /> <span>Edit</span>
                </>
              )}
            </button>
          )}
          {isEditable && (
            <div className="account-settings-group-header-actions">
              <button type="button" onClick={() => cancelEdit()}>
                <span>Cancel</span>
              </button>
              <button
                className="primary-btn"
                disabled={isDisabled()}
                onClick={() => handleSubmit()}
              >
                <CheckIcon />
                <span>Save</span>
              </button>
            </div>
          )}
        </div>
        <div className="form-container">
          <LabelWrapper
            label="Full Name"
            errorMessage={form.getError("displayName")}
          >
            <Controller
              name="displayName"
              control={form.control}
              rules={validation.displayName}
              component={
                <input
                  type="text"
                  placeholder="Full Name"
                  disabled={!isEditable}
                  required
                />
              }
            />
          </LabelWrapper>
          <LabelWrapper
            label="Email Address"
            errorMessage={form.getError("email")}
          >
            <Controller
              name="email"
              control={form.control}
              rules={validation.emailAddress}
              component={
                <input
                  type="email"
                  placeholder="Email Address"
                  disabled={!isEditable}
                  required
                />
              }
            />
          </LabelWrapper>
          <Password />
        </div>
      </StyledProfile>
      <InputDialog
        isOpen={showPasswordConfirmation}
        onConfirm={() => handleSubmit()}
        title="Confirm Your Password"
        value={password}
        inputProps={{ type: "password" }}
        confirmButtonText="Update Profile"
        onChange={(value) => setPassword(value)}
        onClose={() => setShowPasswordConfirmation(false)}
      />
    </>
  );
};

export default Profile;
